import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { nuclearn_api_url } from "src/App";

// ===========================
// Types
// ===========================

export interface PartsAIFeatureSheetRow {
  id?: string | number;
  feature: string;
  value: string;
}

export interface PartsAIFeatureSheetResponse {
  parts_ai_run_fk: number;
  create_user_fk?: number | null;
  features: Record<string, any>;
  feature_order: string[];
}

export interface PartsAIFeatureSheetFieldUpdate {
  field_name: string;
  field_value: any;
}

export interface PartsAIFeatureSheetUpdate {
  features: Record<string, any>;
}

const FEATURE_SHEET_QUERY_KEY = "partsAIFeatureSheet";

// ===========================
// API Functions
// ===========================

/**
 * Fetch the feature sheet for a run
 */
const fetchFeatureSheetByRunId = async (
  runId: number,
): Promise<PartsAIFeatureSheetResponse> => {
  const response = await axios.get<PartsAIFeatureSheetResponse>(
    `${nuclearn_api_url}/products/parts/runs/${runId}/feature-sheet`,
  );
  return response.data;
};

/**
 * Update a single feature field in the feature sheet by run ID (partial update)
 */
const updateFeatureSheetFieldByRunId = async (
  runId: number,
  fieldName: string,
  fieldValue: any,
): Promise<PartsAIFeatureSheetResponse> => {
  const response = await axios.patch<PartsAIFeatureSheetResponse>(
    `${nuclearn_api_url}/products/parts/runs/${runId}/feature-sheet`,
    {
      field_name: fieldName,
      field_value: fieldValue,
    } as PartsAIFeatureSheetFieldUpdate,
  );
  return response.data;
};

/**
 * Replace the entire feature sheet content for a run (full replacement)
 */
const replaceFeatureSheetByRunId = async (
  runId: number,
  features: Record<string, any>,
): Promise<PartsAIFeatureSheetResponse> => {
  const response = await axios.put<PartsAIFeatureSheetResponse>(
    `${nuclearn_api_url}/products/parts/runs/${runId}/feature-sheet`,
    {
      features,
    } as PartsAIFeatureSheetUpdate,
  );
  return response.data;
};

// ===========================
// React Query Hooks
// ===========================

/**
 * React Query hook to fetch the feature sheet for a run
 */
export const useGetFeatureSheet = (runId: number | undefined) => {
  return useQuery(
    [FEATURE_SHEET_QUERY_KEY, runId],
    () => fetchFeatureSheetByRunId(runId!),
    {
      enabled: Boolean(runId),
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  );
};

/**
 * React Query mutation hook to update a single feature field by run ID
 */
export const useUpdateFeatureSheet = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      runId,
      fieldName,
      fieldValue,
    }: {
      runId: number;
      fieldName: string;
      fieldValue: any;
    }) => updateFeatureSheetFieldByRunId(runId, fieldName, fieldValue),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([
          FEATURE_SHEET_QUERY_KEY,
          variables.runId,
        ]);
      },
      onError: (error) => {
        console.error("Error updating feature sheet:", error);
      },
    },
  );
};

/**
 * React Query mutation hook to replace the entire feature sheet by run ID
 */
export const useReplaceFeatureSheet = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ runId, features }: { runId: number; features: Record<string, any> }) =>
      replaceFeatureSheetByRunId(runId, features),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([
          FEATURE_SHEET_QUERY_KEY,
          variables.runId,
        ]);
      },
      onError: (error) => {
        console.error("Error replacing feature sheet:", error);
      },
    },
  );
};
